import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode };

type State = { error: Error | null };

export class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[transporter-web]", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      const { message, stack } = this.state.error;
      const dev = import.meta.env.DEV;
      return (
        <div
          className="min-h-screen bg-[var(--page-bg)] px-6 py-10 text-left font-sans text-[var(--text-body)]"
          role="alert"
        >
          <h1 className="text-xl font-semibold text-red">Something went wrong</h1>
          <p className="mt-4 max-w-2xl text-sm text-gray">
            {dev ? (
              <>
                Check the browser console. A common fix is a valid Clerk publishable key in your environment.
              </>
            ) : (
              <>Please refresh the page. If this keeps happening, contact support.</>
            )}
          </p>
          {dev ? (
            <>
              <pre className="td-surface mt-6 max-w-3xl overflow-x-auto p-6 text-sm text-red">{message}</pre>
              {stack ? (
                <pre className="mt-4 max-w-3xl overflow-x-auto text-xs text-gray-light">{stack}</pre>
              ) : null}
            </>
          ) : null}
        </div>
      );
    }
    return this.props.children;
  }
}
